import { Link } from "react-router-dom";
import { Download, ArrowRight } from "lucide-react";
import ServiceLayout, { ContentSection } from "@/components/site/ServiceLayout";
import { Reveal, TickList } from "@/components/site/Reveal";
import { API, FAMILY_LAW_FORM_URL } from "@/data/services";

const SERVICES_LIST = [
  "Family reports",
  "Parental capacity assessments",
  "Parent–child observations",
  "Child-focused assessments",
  "File review and collateral consultation",
  "Professional recommendations",
];

const PROCESS = [
  { n: "01", title: "Initial interviews", text: "A one-hour interview with each parent, heard separately." },
  { n: "02", title: "Parent–child observations", text: "A one-hour observation of each parent with the child." },
  { n: "03", title: "Child-only session", text: "A one-hour session with only the child, so their responses cannot be influenced by either parent." },
  { n: "04", title: "Completed report", text: "The findings are brought together in a written report with professional recommendations." },
];

const PRACTICAL = [
  { q: "Areas serviced", a: "Gold Coast, Logan, Brisbane and surrounding areas, with interviews held at our Ashmore rooms, in the community or by secure video where appropriate." },
  { q: "Documentation required", a: "Court orders, relevant affidavits or filed material, existing reports or assessments, and a letter of instruction outlining the questions the report should address." },
  { q: "Timeframes", a: "Timeframes are confirmed on engagement and depend on the availability of parties and the completeness of documentation provided." },
  { q: "Fees", a: "Family reports and parental capacity assessments are currently charged at $4,200 plus GST, payable as set out in the fee agreement." },
  { q: "Cancellation terms", a: "A cancellation fee may apply where appointments are cancelled at short notice, as outlined in the fee agreement." },
  { q: "Court attendance", a: "Where court attendance is required, it attracts an additional fee quoted on request." },
];

export default function FamilyLawReports() {
  return (
    <ServiceLayout
      eyebrow="Independent assessment services"
      title="Family Law Reports"
      lead="Independent family reports and parental capacity assessments for parents, legal practitioners and other family law professionals."
      formal
    >
      <ContentSection eyebrow="Services" title="Assessment services">
        <TickList items={SERVICES_LIST} />
        <Reveal delay={0.1}>
          <div className="mt-10 max-w-3xl rounded-2xl border border-euca-line bg-white p-7 sm:p-8" data-testid="family-law-boundary-note">
            <p className="text-base leading-relaxed text-charcoal/85">
              Independent assessment services are kept separate from therapeutic services. A person who receives an
              independent assessment would not ordinarily receive ongoing therapy from the same practitioner, due to
              professional role boundaries.
            </p>
          </div>
        </Reveal>
      </ContentSection>

      <ContentSection eyebrow="The assessor" title="Qualifications and experience" pale>
        <Reveal>
          <p className="max-w-3xl text-base sm:text-lg leading-relaxed text-mutedsoft">
            Assessments are completed by Luciana Adelida Isla Tursicova, an Accredited Mental Health Social Worker
            and registered Specialist Behaviour Support Practitioner. Luciana holds a Bachelor of Social Science
            (Psychology), a Graduate Diploma of Family Dispute Resolution Practice, a Master of Social Work and a
            Master of Science in Medicine (Trauma-Informed Psychotherapy), and is currently a Doctor of Arts
            (Psychology) candidate.
          </p>
          <p className="mt-4 max-w-3xl text-base sm:text-lg leading-relaxed text-mutedsoft">
            She has worked within the child protection sector since 2015 — including alongside the NSW Police Force
            within a Joint Child Protection Response Team — and previously held Clinical Manager positions with
            headspace Beaudesert and headspace Dubbo. Her areas of practice include complex trauma, complex mental
            health presentations, neurodivergence, family functioning and parental capacity. Full professional
            history is on the{" "}
            <Link to="/about" className="font-semibold text-euca hover:underline" data-testid="family-law-about-link">
              About page
            </Link>
            , and a current CV can be provided to referrers on request.
          </p>
        </Reveal>
      </ContentSection>

      <ContentSection eyebrow="The process" title="How the assessment runs">
        <div className="grid gap-6 sm:grid-cols-2" data-testid="family-law-process">
          {PROCESS.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.06}>
              <div className="h-full rounded-2xl border border-euca-line bg-white p-7 shadow-sm">
                <span className="font-serif text-3xl italic text-euca-sage/70">{p.n}</span>
                <h3 className="mt-4 font-serif text-xl font-medium text-euca-dark">{p.title}</h3>
                <p className="mt-2 text-base leading-relaxed text-mutedsoft">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </ContentSection>

      <ContentSection eyebrow="Practical details" title="Fees, timeframes and terms" pale>
        <div className="max-w-3xl divide-y divide-euca-line rounded-2xl border border-euca-line bg-white" data-testid="family-law-practical">
          {PRACTICAL.map((item) => (
            <div key={item.q} className="grid gap-1 p-6 sm:grid-cols-3 sm:gap-6 sm:p-7">
              <h3 className="font-serif text-lg font-medium text-euca-dark">{item.q}</h3>
              <p className="text-base leading-relaxed text-mutedsoft sm:col-span-2">{item.a}</p>
            </div>
          ))}
        </div>
      </ContentSection>

      <ContentSection eyebrow="Make a referral" title="Request an assessment">
        <Reveal>
          <p className="max-w-3xl text-base leading-relaxed text-mutedsoft">
            Solicitors, parties and professional referrers can request an assessment by sending a brief outline of
            the matter. An engagement letter and fee agreement will be provided before any work begins.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href={FAMILY_LAW_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="family-law-request-button"
              className="inline-flex items-center gap-2 rounded-full bg-euca px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-euca-dark"
            >
              Request a Family Law Assessment <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href={`${API}/downloads/family-law-referrer-info`}
              data-testid="family-law-download-button"
              className="inline-flex items-center gap-2 rounded-full border border-euca px-7 py-3.5 text-sm font-semibold text-euca transition-all duration-300 hover:bg-euca hover:text-white"
            >
              Download Information for Referrers <Download className="h-4 w-4" />
            </a>
          </div>
        </Reveal>
      </ContentSection>
    </ServiceLayout>
  );
}
